import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Analytics.css';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get('/api/analytics/dashboard');
      setAnalytics(response.data || {});
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (!analytics) {
    return <div className="error">Error loading analytics</div>;
  }

  const { summary = {}, dailyAttendance = [], courseAttendance = [] } = analytics;
  
  // Ensure summary has default values
  const safeSummary = {
    totalTrainees: summary?.totalTrainees || 0,
    activeTrainees: summary?.activeTrainees || 0,
    inactiveTrainees: summary?.inactiveTrainees || 0,
    totalAttendance: summary?.totalAttendance || 0,
    attendancePercentage: summary?.attendancePercentage || 0,
    presentCount: summary?.presentCount || 0,
    absentCount: summary?.absentCount || 0,
    totalCourses: summary?.totalCourses || 0,
    totalSMS: summary?.totalSMS || 0,
    sentSMS: summary?.sentSMS || 0,
    totalEmails: summary?.totalEmails || 0,
    sentEmails: summary?.sentEmails || 0,
  };

  const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#f59e0b'];

  return (
    <div className="analytics">
      <h1>Analytics Dashboard</h1>

      <div className="analytics-summary">
        <div className="summary-card">
          <h3>Total Trainees</h3>
          <p className="summary-number">{safeSummary.totalTrainees}</p>
          <p className="summary-subtitle">{safeSummary.activeTrainees} active, {safeSummary.inactiveTrainees} inactive</p>
        </div>
        <div className="summary-card">
          <h3>Attendance Rate</h3>
          <p className="summary-number">{safeSummary.attendancePercentage}%</p>
          <p className="summary-subtitle">{safeSummary.presentCount} present / {safeSummary.totalAttendance} total</p>
        </div>
        <div className="summary-card">
          <h3>Messages Sent</h3>
          <p className="summary-number">{safeSummary.sentSMS + safeSummary.sentEmails}</p>
          <p className="summary-subtitle">{safeSummary.sentSMS} SMS, {safeSummary.sentEmails} Emails</p>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h2>Daily Attendance (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={Array.isArray(dailyAttendance) ? dailyAttendance : []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="present" stroke="#16a34a" name="Present" />
              <Line type="monotone" dataKey="absent" stroke="#dc2626" name="Absent" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Course Attendance Rates</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={Array.isArray(courseAttendance) ? courseAttendance : []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="course_title" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="attendance_rate" fill="#2563eb" name="Attendance Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Attendance Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Present', value: safeSummary.presentCount },
                  { name: 'Absent', value: safeSummary.absentCount }
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {[safeSummary.presentCount, safeSummary.absentCount].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h2>Course Attendance Details</h2>
          <div className="course-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Sessions</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Rate</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(courseAttendance) && courseAttendance.map((course, index) => (
                  <tr key={index}>
                    <td>{course.course_title}</td>
                    <td>{course.sessions}</td>
                    <td>{course.present}</td>
                    <td>{course.absent}</td>
                    <td>
                      <span className={`rate-badge ${course.attendance_rate >= 80 ? 'high' : course.attendance_rate >= 60 ? 'medium' : 'low'}`}>
                        {course.attendance_rate || 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

